import { useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import { AppLogo } from '../../../../components/SVGIcons/AppLogo/AppLogo';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { analytics } from '../../../../services/analytics';
import { useStores } from '../../../../stores/useStores';
import { getErrorMessage } from '../../../../utils/errors';

import classes from './EmailVerificationLanding.module.scss';
import envelope from './envelope.webp';

interface EmailVerificationLandingProps {
  onVerificationComplete: () => void;
}

export const EmailVerificationLanding = observer(({ onVerificationComplete }: EmailVerificationLandingProps) => {
  const { t } = useTranslation();
  const { authStore } = useStores();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [error, setError] = useState<string>('');
  const { sendVerificationEmailCooldown } = authStore;

  const handleResend = async () => {
    try {
      setError('');
      await authStore.resendEmailVerification();
    } catch (e) {
      const msg = getErrorMessage(e);
      setError(msg);
    }
  };

  useEffect(() => {
    void analytics.sendEvent('landing_page', {
      landing_type: 'direct_reg_email_verification',
      utm_promo: authStore.getUtmPromo(),
    });

    const unsub = authStore.trackEmailVerificationCompletion(() => {
      onVerificationComplete();
    });

    return () => {
      unsub();
      authStore.clearSendVerificationEmailCooldown();
    };
  }, [authStore, onVerificationComplete]);

  return (
    <div className={classes.emailVerificationLandingContainer}>
      <div className={classes.emailVerificationLanding}>
        <div className={classes.logoSection}>
          <AppLogo
            alwaysColorful
            withText
            textClassname={classes.logoText}
            textColor="#fff"
            width={isMobile ? 58 : 82}
          />
        </div>

        <div className={classes.contentSection}>
          <div className={classes.title} data-testid="email-verification-title">
            {t('auth.checkYourEmail')}!
          </div>

          <div className={classes.subtitle}>
            {t('emailVerification.verifyYourAccount')}
            <br />
            {t('emailVerification.getyour')}
            <span className={classes.welcomeBonus}> {t('emailVerification.getWelcomeBonusSuffix')}</span>
          </div>

          <div className={classes.envelopeContainer}>
            <img alt="envelope" className={classes.envelopeImage} src={envelope} />
          </div>

          <div className={classes.emailSentMessage}>
            {t('emailVerification.confirmationEmailSent')}{' '}
            <span className={classes.emailAddress}>{authStore.currentUserEmail}</span>
          </div>

          <div className={classes.instructionMessage}>
            {t('emailVerification.checkSpamFolder')}
            <br />
            {t('emailVerification.checkSpamFolderSuffix')}
          </div>

          <div className={classes.resendSection}>
            <span className={classes.noEmailText}>{t('emailVerification.didntReceive')}</span>{' '}
            {sendVerificationEmailCooldown > 0 ? (
              <span className={classes.resendDisabled}>
                {t('emailVerification.resendUpperCase')} {sendVerificationEmailCooldown}
                {t('landing.direct.emailVerification.seconds')}
              </span>
            ) : (
              <span className={classes.resendLink} onClick={handleResend}>
                {t('emailVerification.resendEmail')}
              </span>
            )}
          </div>

          <ErrorMessage noSpaceWhenEmpty className={classes.errorMessage}>
            {error}
          </ErrorMessage>
        </div>
      </div>
    </div>
  );
});
